/**
 * CollectorOrchestrator - Centralized event routing for stats collection
 *
 * Layer 1: Data Collection
 *
 * Purpose:
 * - Provide single entry point for all discipline events
 * - Route events to appropriate collectors
 * - Handle initialization and dependency injection
 * - Manage collector lifecycle
 *
 * Architecture:
 * - Services (MentionService, ConsequenceService) call orchestrator
 * - Orchestrator routes to specific collectors
 * - Collectors extract dimensions and forward to storage
 * - Storage persists to Firestore
 *
 * Integration Points:
 * - MentionService.issueMention() -> orchestrator.onMentionCreated()
 * - ConsequenceService.createDisciplineConsequence() -> orchestrator.onConsequenceCreated()
 *
 * Error Handling:
 * - All collection is non-blocking
 * - Failures are logged but don't affect main operations
 * - Stats are important but not critical
 */

import { MentionCollector } from './MentionCollector.js';
import { ConsequenceCollector } from './ConsequenceCollector.js';

export class CollectorOrchestrator {
  /**
   * @param {Object} storageService - Layer 2 storage service (injected)
   */
  constructor(storageService) {
    if (!storageService) {
      throw new Error('CollectorOrchestrator requires a storage service');
    }

    this.storage = storageService;
    this.mentionCollector = new MentionCollector(storageService);
    this.consequenceCollector = new ConsequenceCollector(storageService);

    this.enabled = true; // Can be toggled via config
    this.log('Orchestrator initialized');
  }

  /**
   * Handle mention creation event
   * Called by MentionService after successful mention creation
   * @param {Object} mention - Created mention object
   * @returns {Promise<void>}
   */
  async onMentionCreated(mention) {
    if (!this.enabled) {
      return;
    }

    if (!mention) {
      console.warn('CollectorOrchestrator: Received null/undefined mention');
      return;
    }

    try {
      this.log('Routing mention to collector', { id: mention.id, studentName: mention.studentName });
      await this.mentionCollector.collect(mention);
    } catch (error) {
      // Collection failures should NOT propagate to calling service
      // Stats are important but not critical to mention creation
      console.error('CollectorOrchestrator: Mention collection failed (non-blocking):', error);
    }
  }

  /**
   * Handle consequence creation event
   * Called by ConsequenceService after successful consequence creation
   * @param {Object} consequence - Created consequence object
   * @returns {Promise<void>}
   */
  async onConsequenceCreated(consequence) {
    if (!this.enabled) {
      return;
    }

    if (!consequence) {
      console.warn('CollectorOrchestrator: Received null/undefined consequence');
      return;
    }

    try {
      this.log('Routing consequence to collector', { id: consequence.id, type: consequence.type });
      await this.consequenceCollector.collect(consequence);
    } catch (error) {
      console.error('CollectorOrchestrator: Consequence collection failed (non-blocking):', error);
    }
  }

  /**
   * Handle mention update event (future enhancement)
   * For tracking when mentions are actioned
   * @param {Object} mention - Updated mention object
   * @returns {Promise<void>}
   */
  async onMentionUpdated(mention) {
    if (!this.enabled) {
      return;
    }

    // Future: Track mention lifecycle changes
    // - actioned: false -> true
    // - triggeredConsequences updated
    // This could feed into time-to-action metrics
  }

  /**
   * Handle consequence update event (future enhancement)
   * For tracking when consequences are actioned
   * @param {Object} consequence - Updated consequence object
   * @returns {Promise<void>}
   */
  async onConsequenceUpdated(consequence) {
    if (!this.enabled) {
      return;
    }

    // Future: Track consequence lifecycle changes
    // - status: pending -> actioned
    // - Calculate time-to-action
    // - Feed into effectiveness metrics
  }

  /**
   * Enable stats collection
   * Useful for testing or emergency disable
   */
  enable() {
    this.enabled = true;
    this.log('Stats collection enabled');
  }

  /**
   * Disable stats collection
   * Useful for testing or emergency disable
   */
  disable() {
    this.enabled = false;
    this.log('Stats collection disabled');
  }

  /**
   * Check if stats collection is enabled
   * @returns {boolean}
   */
  isEnabled() {
    return this.enabled;
  }

  /**
   * Get collector health status
   * @returns {Object}
   */
  getHealth() {
    return {
      enabled: this.enabled,
      mentionCollectorReady: !!this.mentionCollector,
      consequenceCollectorReady: !!this.consequenceCollector,
      storageReady: !!this.storage
    };
  }

  /**
   * Log orchestrator activity
   * @param {string} message - Log message
   * @param {Object} data - Additional data
   */
  log(message, data = {}) {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[CollectorOrchestrator] ${message}`, data);
    }
  }
}
